/** Targeted homework (STORY-063): propose a draft from the mastery map, review the clusters
 * of students with the same weak objectives and the items each will get, release per cluster
 * or per student. Names come from the roster; analytics only knows person ids. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "../api/client";
import { TeacherChrome } from "../teacher/Chrome";
import { getCourse, getMastery, getRoster, isOfflineError, listAssignments, proposeAssignment, releaseAssignment, type Assignment } from "../teacher/api";
import { Button, DraftChip } from "../ui";

export function Homework() {
  const { courseId } = useParams({ strict: false }) as { courseId: string };
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const course = useQuery({ queryKey: ["course", courseId], queryFn: () => getCourse(courseId) });
  const roster = useQuery({ queryKey: ["roster", courseId], queryFn: () => getRoster(courseId) });
  const matrix = useQuery({ queryKey: ["mastery", courseId], queryFn: () => getMastery(courseId), retry: false });
  const assignments = useQuery({ queryKey: ["assignments", courseId], queryFn: () => listAssignments(courseId), retry: false });
  const [title, setTitle] = useState("");
  const [itemsPerLo, setItemsPerLo] = useState(2);
  const [open, setOpen] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const names = useMemo(() => new Map((roster.data ?? []).map((p) => [p.person_id, p.label])), [roster.data]);
  const codes = useMemo(() => new Map((matrix.data?.los ?? []).map((lo) => [lo.lo_id, lo.code])), [matrix.data]);
  const refresh = () => void qc.invalidateQueries({ queryKey: ["assignments", courseId] });
  const propose = useMutation({
    mutationFn: () => proposeAssignment(courseId, { title, items_per_lo: itemsPerLo }),
    onSuccess: (a) => { setMsg(null); setOpen(a.assignment_id); refresh(); },
    onError: (e) => setMsg(e instanceof ApiError && e.code === "no_targets" ? t("hw.noTargets") : String(e)),
  });
  const release = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof releaseAssignment>[1] }) => releaseAssignment(id, body),
    onSuccess: refresh,
  });
  const label = (personId: string) => names.get(personId) || t("hw.anon", { id: personId.slice(-4) });
  const code = (loId: string) => codes.get(loId) ?? loId;
  const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(i18n.language, { day: "numeric", month: "short" }) : "–");
  const card = "rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm";
  const notProjected = assignments.error instanceof ApiError && assignments.error.status === 404;
  const needsServer = isOfflineError(assignments.error);

  return (
    <TeacherChrome title={t("hw.title")} course={course.data ? { id: courseId, title: course.data.title } : null} stage="dashboard" wide>
      {/* The chrome prints the title; the page adds the lede, not a second heading. */}
      <p className="measure -mt-2 text-sm text-[color:var(--ink-2)]">{t("hw.lede")}</p>

      <section className={card} aria-labelledby="hw-propose">
        <h2 id="hw-propose" className="font-semibold">{t("hw.propose")}</h2>
        <p className="measure mb-3 text-sm text-[color:var(--ink-2)]">{t("hw.proposeHint")}</p>
        <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); propose.mutate(); }}>
          <label className="flex flex-col gap-1 text-sm">{t("hw.titleField")}<input className="field" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} /></label>
          <label className="flex flex-col gap-1 text-sm">{t("hw.itemsPerLo")}
            <select className="field" value={itemsPerLo} onChange={(e) => setItemsPerLo(Number(e.target.value))}>
              {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <Button type="submit" loading={propose.isPending}>{t("hw.proposeCta")}</Button>
        </form>
        {msg && <p role="alert" className="mt-2 text-sm">{msg}</p>}
      </section>

      {notProjected && <p className={card}>{t("hw.notProjected")}</p>}
      {needsServer && <p className={card}>{t("dash.needsServer")}</p>}
      {assignments.data && assignments.data.length === 0 && <p className={card}>{t("hw.none")}</p>}

      <ul className="flex flex-col gap-3">
        {(assignments.data ?? []).map((a: Assignment) => {
          const expanded = open === a.assignment_id;
          const itemStem = (id: string) => a.items.find((i) => i.item_id === id)?.stem ?? id;
          return (
            <li key={a.assignment_id} className={card}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h2 className="font-semibold">{a.title || t("hw.untitled", { date: fmt(a.created_at) })}</h2>
                  <p className="text-xs text-[color:var(--ink-2)]">
                    {t(`hw.statuses.${a.status}`)} · {t("hw.counts", { released: a.released, total: a.targets.length, clusters: a.clusters.length })}
                    {a.due_date ? ` · ${t("hw.due", { date: fmt(a.due_date) })}` : ""}
                  </p>
                </div>
                <div className="flex gap-2">
                  {a.released < a.targets.length && (
                    <Button variant="secondary" loading={release.isPending} onClick={() => release.mutate({ id: a.assignment_id, body: { everyone: true } })}>{t("hw.releaseAll")}</Button>
                  )}
                  <Button variant="tertiary" onClick={() => setOpen(expanded ? null : a.assignment_id)} aria-expanded={expanded}>{expanded ? t("hw.hide") : t("hw.review")}</Button>
                </div>
              </div>
              {a.status === "draft" && <div className="mt-2"><DraftChip /></div>}
              {expanded && (
                <ul className="mt-3 flex flex-col gap-3" aria-label={t("hw.clusters")}>
                  {a.clusters.map((c) => {
                    const members = a.targets.filter((x) => x.cluster_key === c.cluster_key);
                    const allReleased = c.released === c.respondents.length;
                    return (
                      <li key={c.cluster_key} className="rounded-[var(--radius-control)] border p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="text-sm">
                            <span className="font-medium">{t("hw.cluster", { count: c.respondents.length })}</span>{" "}
                            <span className="text-[color:var(--ink-2)]">{t("hw.weakOn")} {c.lo_ids.map(code).join(", ")}</span>
                          </p>
                          <Button variant="secondary" disabled={allReleased} loading={release.isPending} onClick={() => release.mutate({ id: a.assignment_id, body: { cluster_keys: [c.cluster_key] } })}>
                            {allReleased ? t("hw.released") : t("hw.releaseCluster")}
                          </Button>
                        </div>
                        <ul className="mt-2 flex flex-col gap-1 text-sm">
                          {members.map((m) => (
                            <li key={m.respondent_id} className="flex flex-wrap items-center gap-2">
                              <span className="min-w-32">{label(m.respondent_id)}</span>
                              <span className="min-w-0 flex-1 text-xs text-[color:var(--ink-2)]">
                                {m.item_ids.map((id) => itemStem(id)).join(" · ")}
                              </span>
                              {m.released_at ? (
                                <span className="text-xs">{t("hw.releasedOn", { date: fmt(m.released_at) })}</span>
                              ) : (
                                <Button variant="tertiary" loading={release.isPending} onClick={() => release.mutate({ id: a.assignment_id, body: { respondent_ids: [m.respondent_id] } })}>{t("hw.releaseOne")}</Button>
                              )}
                            </li>
                          ))}
                        </ul>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </TeacherChrome>
  );
}
